import { useState, useEffect, useMemo, useCallback, useImperativeHandle, RefObject } from "react";
import { useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import i18n from "i18next";
//import cn from "classnames";
import Switch from '@components/new/Switch';
import { H4, Title3, SubTitle3 } from "@components/new/Typo";
// import { Button, ButtonSize, ButtonType } from "@components/new/Button";
// import { IconButton, IconButtonSize, IconButtonType } from "@components/new/IconButton";
// import ItemSearchItem from "../../components/ItemSearchItem";
import { TabsContent, TabsList } from '@components/Tabs/Tabs.tsx';
import { Table, TableColumn } from "@components/new/Table";
import DataTable from '@containers/DataTableNew';
// import { Tooltip } from '@components/new/Tooltip';
import {
  createFBPWithdrawalRequest,
  isWbRequestItem,
  isYmRequestItem,
  getFBPStocks,
  TFBPStockItem,
  TYmItem,
  TWbItem,
  TRequestParams,
} from "@api/apiClient/fbp";
import getItemsColumns from "./linkItemsColumns";
import getMatchingColumns from "./matchingColumns";
import { FormHandle, TLinkRow } from "./types";
// import NoItemsIcon from "@assets/icons/hugeicons/package-remove.svg?react";
import styles from './Form.module.scss';

type TWbItemWithCount = TWbItem & { present?: number }
type TYmItemWithCount = TYmItem & { present?: number }

const MAX_ITEMS = 100;

const Form = ({
  formRef,
  onStatusChange,
  onSuccess,
  onError,
  sellerWarehouseId,
  stores,
}: {
  formRef?: RefObject<FormHandle>;
  onStatusChange: (props: {
    disabled: boolean,
    isLoading?: boolean,
  }, msg?: React.ReactNode) => void;
  onSuccess: () => void;
  onError: () => void;
  sellerWarehouseId: string;
  stores?: string[];
  // fbpWarehouseId: string;
}) => {
  const currentLanguage = i18n.language;
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("items");
  const [showUnlicnkedOnly, setShowUnlicnkedOnly] = useState(false);
  const [selectedItems, setSelectedItems] = useState<TLinkRow[]>([]);

  const withdrawalMutation = useMutation({
    mutationFn: createFBPWithdrawalRequest,
    onSuccess: () => onSuccess(),
    onError: () => onError(),
  });

  const stocksGetter = useCallback(
    ({ filters, sorting, page }: TRequestParams) => {
      return getFBPStocks({
        filters,
        sorting,
        page,
        sellerWarehouseId,
        hasStock: true,
      }).then((data) => ({
        ...data,
        items: data.items.reduce((acc: TLinkRow[], item: TFBPStockItem) => {
          return [
            ...acc,
            ...item.ymItems.map(v => ({
              ...v.ymProduct,
              present: Math.min(v.present ?? 0, 99999999),
              selected: false, // !item.matchingLinkName,
              matchingLinkName: item.matchingLinkName,
            })),
            ...item.wbItems.map(v => ({
              ...v.wbProduct,
              present: Math.min(v.present ?? 0, 99999999),
              selected: false, // !item.matchingLinkName,
              matchingLinkName: item.matchingLinkName,
            })),
          ];
        }, [])
      }));
    },
    [sellerWarehouseId]
  );

  const columns: TableColumn<TLinkRow>[] = useMemo(() => getItemsColumns(
    t,
  ), [currentLanguage]);

  const columnsMatching: TableColumn<TLinkRow>[] = useMemo(() => getMatchingColumns(
    t,
    // sellerWarehouseId,
  ), [currentLanguage, sellerWarehouseId]);

  console.log(showUnlicnkedOnly) // todo

  const handleSelectionChange = useCallback((
    selectedIds: string[],
    items: TLinkRow[],
  ) => {
    setSelectedItems((oldSelected: TLinkRow[]) => (
      selectedIds.reduce((acc: TLinkRow[], s: string) => {
        const item = oldSelected.find(v => v.id === s) || items.find(v => v.id === s);
        if (item) return [...acc, item];
        return acc;
      }, [])
    ));
  }, []);

  const tabs = [
    {
      id: 'items',
      title: t('fbpPage.delStoreModal.tabItems'),
      content: (
        <div className={styles.content}>
          {false && <Switch
            checked={showUnlicnkedOnly}
            onChange={() => setShowUnlicnkedOnly(!showUnlicnkedOnly)}
            title={t('fbpPage.delStoreModal.unLinkedItems')}
          />}

          <DataTable<TLinkRow>
            selectable
            isCheckboxDisabled={(data: TLinkRow) => (selectedItems.length >= MAX_ITEMS && !selectedItems.find(v => data.id === v.id))}
            checkboxTooltip={(data: TLinkRow) => ((selectedItems.length >= MAX_ITEMS && !selectedItems.find(v => data.id === v.id)) ? t("fbpPage.delStoreModal.checkboxMaxCountTooltip") : "")}
            selectedIds={selectedItems.map(v => v.id)}
            columns={columns}
            defaultPageSize={MAX_ITEMS}
            filters={[]}
            queryKey={["getFBPStocks", sellerWarehouseId, "hasStock"]}
            onSelectionChange={handleSelectionChange}
            getData={stocksGetter}
            verticalAlignment="center"
            noLoaderOnRefresh
            pending={!sellerWarehouseId}
            hidePaginationDropdown
            maxSelected={MAX_ITEMS}
            resetOnUpdate={false}
            keepSearchParams={false}
            keepSelectionOnFiltersChange
            pageSizeOptions={[MAX_ITEMS]}
          />
        </div>
      ),
    },
    {
      id: 'matchings',
      title: t('fbpPage.delStoreModal.tabMatchings'),
      content: (
        <div className={styles.content}>
          <Title3>{t("fbpPage.delStoreModal.matchingsDescription")}</Title3>

          <Table<TLinkRow>
            columns={columnsMatching}
            data={[]}
          />
        </div>
      ),
    },
  ];

  const getIsValid = () => {
    return (selectedItems?.length > 0) &&
      (selectedItems?.length <= 100);
  }

  useEffect(() => {
    onStatusChange({
      disabled: !getIsValid(),
      isLoading: Boolean(withdrawalMutation.isLoading),
    });
  }, [withdrawalMutation.isLoading, selectedItems])

  const onSubmit = () => {
    withdrawalMutation.mutate({
      sellerWarehouseId,
      ymProducts: (selectedItems.filter(v => (isYmRequestItem(v))) as TYmItemWithCount[]).map(v => ({
        ymProductId: v.id,
        count: v.present ?? 0,
      })),
      wbProducts: (selectedItems.filter(v => (isWbRequestItem(v))) as TWbItemWithCount[]).map(v => ({
        wbProductId: v.id,
        count: v.present ?? 0,
      })),
    });
  };

  useImperativeHandle(formRef, () => ({
    submit: onSubmit,
  }));

  const isMatchingAvailable = false; // todo

  return (
    <div className={styles.form}>
      {isMatchingAvailable ? (
        <TabsList
          onChange={setActiveTab}
          tabs={tabs}
          value={activeTab}
        />
      ) : (
        <div className={styles.title}>
          <H4>{t('fbpPage.delStoreModal.itemsTitle')}</H4>
          {stores?.length ? (
            stores.length === 1 ? (
              <SubTitle3 color="grey-800">
                {t('fbpPage.delStoreModal.itemsDescriptionWithStore', { store: stores[0] })}
              </SubTitle3>
            ) : (
              <SubTitle3 color="grey-800">{
                t('fbpPage.delStoreModal.itemsDescriptionWithStores', {
                  stores: stores.slice(0, -1).join(", "),
                  lastStore: stores[stores.length - 1],
                })
              }</SubTitle3>
            )
          ) : (
            <SubTitle3 color="grey-800">{t('fbpPage.delStoreModal.itemsDescription')}</SubTitle3>
          )}
        </div>
      )}

      <TabsContent
        tab={tabs.find((tab) => (tab.id === activeTab))}
      />
    </div>
  )
};

export default Form;
